import { useMemo } from "react"
import PropTypes from "prop-types"
import { size } from "lodash"
import classnames from "classnames"
import { OfflineBolt } from "@mui/icons-material"
import ModelSelectorAutocomplete from "connected-components/model-selector-autocomplete"
import Tooltip from "components/material/tooltip"
import { DROPDOWN_OPTION } from "helpers/shapes"
import { LOADING, OK } from "helpers/data-statuses"
import Error from "components/material/error"
import ModelChange from "./components/model-change"
import "./styles.scss"

export default function ModelSelector({
  status,
  modelId,
  modelName,
  sortedModelsByCoverage,
  setModel,
  resourceType,
  resourceId,
  disablePortal,
  disableUnderline,
  disabled,
  warningMessage,
  alternateLayout,
  modifiedScreens,
  openDialog,
  additionalOption,
}) {
  const handleModelIdSelected = (selectedModelId) => {
    if (!selectedModelId) {
      return
    }

    if (selectedModelId === "email") {
      const mail = document.createElement("a")
      mail.href = "mailto:customersuccess@ompnt.com"
      mail.click()
      return
    }

    if (size(modifiedScreens) > 0) {
      openDialog({
        title: "Confirm Model Change",
        content: <ModelChange modifiedScreens={modifiedScreens} changeModel={() => setModel(selectedModelId)} />,
      })
    } else {
      setModel(selectedModelId)
    }
  }

  const requiresResourceCoverage = !!(resourceType && resourceId)
  const resourceTypeLabel = resourceType === "swap" ? "basket" : resourceType

  const listOptions = useMemo(() => {
    const options = []

    if (additionalOption) {
      options.push({ id: additionalOption.value, name: additionalOption.label, rowType: "additional" })
    }

    if (status === LOADING) {
      options.push({ id: modelId, name: modelName, rowType: "model", hasCoverage: true })
    }

    if (status === OK) {
      sortedModelsByCoverage.forEach((model) => {
        options.push({ ...model, rowType: "model" })
      })
    }

    options.push({ id: "email", rowType: "email", name: "Email us" })

    return options
  }, [additionalOption, status, sortedModelsByCoverage, modelId, modelName])

  let modelSelector = null

  switch (status) {
    case LOADING:
    case OK:
      modelSelector = (
        <ModelSelectorAutocomplete
          className="model-selector"
          models={listOptions}
          value={modelId}
          displayNameFallback={modelName}
          onChange={handleModelIdSelected}
          disabled={disabled}
          loading={status === LOADING}
          requiresResourceCoverage={requiresResourceCoverage}
          resourceTypeLabel={resourceTypeLabel || "resource"}
          disableUnderline={disableUnderline}
          disablePortal={disablePortal}
          popperPlacement="bottom-end"
        />
      )
      break
    default:
      modelSelector = <Error text="There was an error while loading the models. Please try again." />
  }

  return <div className="modules_model-selector">
    {warningMessage && <Tooltip title={warningMessage} classes={{ tooltip: "warning-tooltip" }}>
      <OfflineBolt className={classnames("warning", { "alternate-layout": alternateLayout })} />
    </Tooltip>}
    {modelSelector}
  </div>
}

ModelSelector.propTypes = {
  modelId: PropTypes.string.isRequired,
  modelName: PropTypes.string.isRequired,
  modifiedScreens: PropTypes.array,
  openDialog: PropTypes.func.isRequired,
  sortedModelsByCoverage: PropTypes.array.isRequired,
  noRestrictionModels: PropTypes.array,
  setModel: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  resourceType: PropTypes.string,
  resourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disablePortal: PropTypes.bool,
  disableUnderline: PropTypes.bool,
  disabled: PropTypes.bool,
  alternateLayout: PropTypes.bool,
  warningMessage: PropTypes.string,
  additionalOption: DROPDOWN_OPTION,
}
